import React from 'react';
import { Chip, FormControl, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Control, Controller } from 'react-hook-form';
import { CREATE_TEAM_INPUT_MAX_LENGTH } from '../../constants';
import { SearchableSelectField } from './SearchableSelectField';
import { TreeSelectField } from './TreeSelectField';
import { CreateTeamFormData, SelectOption, TreeNode } from './types';

function parentTeamHelperText(
  errorMessage: string | undefined,
  isParentTeamReadOnly: boolean,
  applicationCount: number,
): string | undefined {
  if (errorMessage) {
    return errorMessage;
  }
  if (isParentTeamReadOnly) {
    return 'Parent team cannot be changed after creation';
  }
  if (applicationCount === 0) {
    return 'Select at least one application first';
  }
  return undefined;
}

interface TeamFormFieldsProps {
  control: Control<CreateTeamFormData>;
  isPending: boolean;
  applicationOptions: SelectOption[];
  parentTeamTree: TreeNode[];
  accountOptions: SelectOption[];
  locationTree: TreeNode[];
  selectedApplications: string[];
  areApplicationsLoading?: boolean;
  isParentTeamLoading?: boolean;
  areModifiersLoading?: boolean;
  isParentTeamReadOnly?: boolean;
}

export function TeamFormFields({
  control,
  isPending,
  applicationOptions,
  parentTeamTree,
  accountOptions,
  locationTree,
  selectedApplications,
  areApplicationsLoading = false,
  isParentTeamLoading = false,
  areModifiersLoading = false,
  isParentTeamReadOnly = false,
}: TeamFormFieldsProps) {
  return (
    <FormControl
      style={{
        display: 'flex',
        width: '100%',
        gap: 32,
        flexDirection: 'column',
        marginBottom: 24,
      }}
    >
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            variant="outlined"
            placeholder="e.g. DC20 Operations"
            required
            label="Name"
            InputLabelProps={{ shrink: true }}
            error={!!error}
            helperText={error?.message}
            disabled={isPending}
            inputProps={{ maxLength: CREATE_TEAM_INPUT_MAX_LENGTH }}
          />
        )}
      />

      <Controller
        name="applications"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Autocomplete
            multiple
            options={applicationOptions}
            value={applicationOptions.filter(application =>
              field.value.includes(application.value),
            )}
            disabled={isPending || areApplicationsLoading}
            loading={areApplicationsLoading}
            getOptionLabel={option => option.label}
            getOptionSelected={(option, selected) =>
              option.value === selected.value
            }
            onChange={(_, options) =>
              field.onChange(options.map(option => option.value))
            }
            filterSelectedOptions
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.label}
                  label={option.label}
                  color="primary"
                />
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label="Applications"
                required
                placeholder={field.value.length ? '' : 'Select applications'}
                InputLabelProps={{ shrink: true }}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        )}
      />

      <Controller
        name="parentTeamId"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TreeSelectField
            label="Parent Team"
            placeholder="Select Team..."
            nodes={parentTeamTree}
            value={field.value}
            onChange={field.onChange}
            required
            disabled={
              isParentTeamReadOnly ||
              selectedApplications.length === 0 ||
              isPending ||
              isParentTeamLoading
            }
            error={!!error}
            helperText={parentTeamHelperText(
              error?.message,
              isParentTeamReadOnly,
              selectedApplications.length,
            )}
          />
        )}
      />

      <Controller
        name="account"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <SearchableSelectField
            label="Account"
            value={field.value}
            options={accountOptions}
            onChange={field.onChange}
            disabled={isPending || areModifiersLoading}
            loading={areModifiersLoading}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      <Controller
        name="locationIds"
        control={control}
        render={({ field }) => (
          <TreeSelectField
            label="Location"
            placeholder="Select location..."
            nodes={locationTree}
            value={field.value}
            onChange={field.onChange}
            multiple
            showCounts
            disabled={isPending || areModifiersLoading}
          />
        )}
      />
    </FormControl>
  );
}
